import CelebrationIcon from "@mui/icons-material/Celebration";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from "@mui/material";
import React from "react";

import {
  Author,
  LeaderboardAuthor,
} from "../../../manifest/models/author.models";
import { PostData } from "../../../posts/post.models";
import { getUsernameFor } from "../../../utils/username";
import "./right-sidebar.component.css";

export interface SponsoredPostData extends PostData {
  url: string;
  image: string;
}

export interface RightSidebarProps {
  sponsored: SponsoredPostData[];
  birthdays: Author[];
  showContacts: boolean;
  contacts: Author[];
  leaderboard?: LeaderboardAuthor[];
}

export function RightSidebar(props: RightSidebarProps) {
  return (
    <div className="right-sidebar-outer">
      <SponsoredSection {...props} />
      <Divider />
      <BirthdaySection {...props} />
      <Divider />
      <ContactSection {...props} />
      <LeaderboardSection {...props} />
    </div>
  );
}

export function SponsoredSection(props: RightSidebarProps) {
  return (
    <List
      id={"right-sidebar-sponsored-section"}
      className={"sponsored-section"}
    >
      <ListItem disablePadding id={"right-sidebar-sponsored-section"}>
        <ListItemText
          primary="Sponsored"
          className="sidebar-section-header"
          id={"right-sidebar-sponsored-section"}
        />
      </ListItem>
      {props.sponsored.map((sponsored) => {
        return (
          <ListItem key={sponsored.id}>
            <div
              className="right-sidebar-post-container"
              id={`${sponsored.id}-sponsored-post`}
            >
              <img className="right-sidebar-post-image" src={sponsored.image} />
              <div
                className="right-sidebar-post-text"
                id={`${sponsored.id}-sponsored-text`}
              >
                <div
                  className="right-sidebar-post-title"
                  id={`${sponsored.id}-sponsored-title`}
                >
                  {sponsored.title}
                </div>
                <div
                  className="right-sidebar-post-url"
                  id={`${sponsored.id}-sponsored-url`}
                >
                  {sponsored.url}
                </div>
              </div>
            </div>
          </ListItem>
        );
      })}
    </List>
  );
}

function contactName(contact: Author): string {
  return getUsernameFor(contact);
}

function birthdaySummary(birthdays: Author[]): React.ReactNode {
  if (birthdays.length == 0) {
    return "No birthdays today.";
  }

  const firstBirthday = contactName(birthdays[0]);
  let body: React.ReactNode[];

  if (birthdays.length == 2) {
    body = [
      <BoldedText key={1} text={firstBirthday} />,
      " and ",
      <BoldedText key={2} text={contactName(birthdays[1])} />,
      " have birthdays today!",
    ];
  } else if (birthdays.length > 2) {
    body = [
      <BoldedText key={1} text={firstBirthday} />,
      " and ",
      <BoldedText key={2} text={`${birthdays.length} others`} />,
      " have birthdays today!",
    ];
  } else {
    body = [
      <BoldedText key={1} text={firstBirthday} />,
      "has a birthday today!",
    ];
  }

  return <span className="birthday-summary">{body}</span>;
}

function BoldedText(props: { text: string }) {
  return (
    <Typography variant="body1" component="span" sx={{ fontWeight: "bold" }}>
      {props.text}
    </Typography>
  );
}

export function BirthdaySection(props: RightSidebarProps) {
  return (
    <List id={"right-sidebar-birthday-section"} className={"birthday-section"}>
      <ListItem disablePadding id={"right-sidebar-birthday-section"}>
        <ListItemText
          primary="Birthdays"
          className="sidebar-section-header"
          id={"right-sidebar-birthday-section"}
        />
      </ListItem>
      <ListItem id={"right-sidebar-birthday-button"}>
        <ListItemAvatar id={"right-sidebar-birthday-button"}>
          <Avatar
            sx={{ backgroundColor: "secondary.light" }}
            id={"right-sidebar-birthday-icon"}
          >
            <CelebrationIcon
              fontSize="large"
              sx={{ color: "primary.light", padding: "0 2px" }}
              id={"right-sidebar-birthday-icon"}
            />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={birthdaySummary(props.birthdays)}
          id={"right-sidebar-birthday-text"}
        />
      </ListItem>
    </List>
  );
}

export function ContactSection(props: RightSidebarProps) {
  if (!props.showContacts) return null;
  return (
    <List id={"right-sidebar-contact-section"} className={"contact-section"}>
      <ListItem disablePadding>
        <ListItemText
          primary="Contacts"
          className="sidebar-section-header"
          id={"right-sidebar-contact-section"}
        />
      </ListItem>
      {props.contacts.map((contact) => {
        return (
          <ListItem
            key={getUsernameFor(contact)}
            id={`right-sidebar-contact-${contact.first}`}
          >
            <ListItemAvatar
              id={`right-sidebar-contact-${contact.first}-avatar`}
            >
              <Avatar
                alt={getUsernameFor(contact)}
                src={contact.avatar.filename}
                id={`right-sidebar-contact-${contact.first}-avatar`}
              />
            </ListItemAvatar>
            <ListItemText
              primary={getUsernameFor(contact)}
              id={`right-sidebar-contact-${contact.first}-name`}
            />
          </ListItem>
        );
      })}
    </List>
  );
}

export function LeaderboardSection(props: RightSidebarProps) {
  if (!props.leaderboard || props.leaderboard.length === 0) return null;

  // Helper to determine rank color/styling
  const getRankStyles = (index: number) => {
    switch (index) {
      case 0:
        return { color: "#FFD700", scale: "1.2" }; // Gold
      case 1:
        return { color: "#C0C0C0", scale: "1.1" }; // Silver
      case 2:
        return { color: "#CD7F32", scale: "1.05" }; // Bronze
      default:
        return { color: "text.secondary", scale: "1" };
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 4,
        // bgcolor: "background.paper",
        // border: "1px solid",
        // borderColor: "divider",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
        <EmojiEventsIcon color="primary" />
        <Typography variant="h6" fontWeight="700" sx={{ fontSize: "1.1rem" }}>
          Leaderboard
        </Typography>
      </Box>

      <List disablePadding>
        {props.leaderboard.map((contact, index) => {
          const rankStyle = getRankStyles(index);
          const username = getUsernameFor(contact);

          return (
            <React.Fragment key={username}>
              <ListItem
                disableGutters
                sx={{
                  py: 1.5,
                  transition: "0.2s",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.02)", borderRadius: 2 },
                }}
              >
                <ListItemAvatar sx={{ minWidth: 48 }}>
                  <Box sx={{ position: "relative" }}>
                    <Avatar
                      src={contact.avatar.filename}
                      alt={username}
                      sx={{
                        width: 40,
                        height: 40,
                        border:
                          index < 3 ? `2px solid ${rankStyle.color}` : "none",
                      }}
                    />
                    {index < 3 && (
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: -5,
                          right: -5,
                          bgcolor: rankStyle.color,
                          borderRadius: "50%",
                          width: 18,
                          height: 18,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "2px solid white",
                          fontSize: "10px",
                          fontWeight: "bold",
                          color: "#fff",
                        }}
                      >
                        {index + 1}
                      </Box>
                    )}
                  </Box>
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight="600" noWrap>
                      {username}
                    </Typography>
                  }
                />

                <Box sx={{ ml: 1 }}>
                  <Chip
                    label={`${contact.points.toLocaleString()} pts`}
                    size="small"
                    variant={index < 3 ? "filled" : "outlined"}
                    color={index === 0 ? "primary" : "default"}
                    sx={{
                      fontWeight: "700",
                      fontSize: "0.75rem",
                      height: 24,
                      background:
                        index === 0
                          ? "linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)"
                          : undefined,
                      color: index === 0 ? "white" : "text.primary",
                      border: index === 0 ? "none" : undefined,
                    }}
                  />
                </Box>
              </ListItem>
              {index < (props.leaderboard?.length ?? 0) - 1 && (
                <Divider
                  variant="inset"
                  component="li"
                  sx={{ ml: "48px", opacity: 0.6 }}
                />
              )}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
}
